<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';
import { INBOX_TYPES, getInboxIconByType } from 'dashboard/helper/inbox';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  inbox: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['action']);

const { t } = useI18n();

const [showActionsDropdown, toggleDropdown] = useToggle();

const inboxName = computed(() => {
  const inbox = props.inbox;
  if (!inbox?.name) {
    return '';
  }

  const isTwilioChannel = inbox.channel_type === INBOX_TYPES.TWILIO;
  const isWhatsAppChannel = inbox.channel_type === INBOX_TYPES.WHATSAPP;
  const isEmailChannel = inbox.channel_type === INBOX_TYPES.EMAIL;

  if (isTwilioChannel || isWhatsAppChannel) {
    const identifier = inbox.messaging_service_sid || inbox.phone_number;
    return identifier ? `${inbox.name} (${identifier})` : inbox.name;
  }

  if (isEmailChannel && inbox.email) {
    return `${inbox.name} (${inbox.email})`;
  }

  return inbox.name;
});

const isAllStages = computed(() => {
  return (
    props.inbox?.all_stages !== false &&
    (!props.inbox?.kanban_stages || props.inbox?.kanban_stages?.length === 0)
  );
});

const kanbanStages = computed(() => {
  return Array.isArray(props.inbox?.kanban_stages)
    ? props.inbox.kanban_stages
    : [];
});

const menuItems = computed(() => [
  {
    label: 'Configurar Etapas do Funil',
    value: 'edit',
    action: 'edit',
    icon: 'i-lucide-sliders',
  },
  {
    label: t('CAPTAIN.INBOXES.OPTIONS.DISCONNECT'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash',
  },
]);

const icon = computed(() => {
  const {
    medium,
    channel_type: type,
    voice_enabled: voiceEnabled,
  } = props.inbox;
  return getInboxIconByType(type, medium, 'outline', voiceEnabled);
});

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id, inbox: props.inbox });
};
</script>

<template>
  <CardLayout>
    <div class="flex flex-col w-full gap-2.5">
      <div class="flex justify-between items-center w-full gap-2">
        <span
          class="text-base font-medium text-n-slate-12 line-clamp-1 flex items-center gap-2"
        >
          <span :class="icon" />
          {{ inboxName }}
        </span>
        <div class="flex items-center gap-2">
          <Policy
            v-on-clickaway="() => toggleDropdown(false)"
            :permissions="['administrator']"
            class="relative flex items-center group"
          >
            <Button
              icon="i-lucide-ellipsis-vertical"
              color="slate"
              size="xs"
              class="rounded-md group-hover:bg-n-alpha-2"
              @click="toggleDropdown()"
            />
            <DropdownMenu
              v-if="showActionsDropdown"
              :menu-items="menuItems"
              class="mt-1 ltr:right-0 rtl:left-0 top-full z-50 min-w-[200px]"
              @action="handleAction($event)"
            />
          </Policy>
        </div>
      </div>

      <!-- Segmentação por Etapas do Kanban -->
      <div class="flex items-center justify-between pt-1 border-t border-n-weak">
        <div class="flex flex-wrap items-center gap-1.5">
          <div
            v-if="isAllStages"
            class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
          >
            <i class="i-lucide-check-circle size-3.5" />
            <span>Todas as etapas do Funil / Kanban</span>
          </div>

          <template v-else>
            <span class="text-xs font-medium text-n-slate-11 mr-1">Etapas:</span>
            <span
              v-for="stage in kanbanStages"
              :key="stage"
              class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-medium bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-500/20"
            >
              <i class="i-lucide-tag size-3" />
              {{ stage }}
            </span>
          </template>
        </div>

        <button
          type="button"
          class="text-xs text-n-brand hover:underline font-medium flex items-center gap-1 ml-auto"
          @click="emit('action', { action: 'edit', id: props.id, inbox: props.inbox })"
        >
          <i class="i-lucide-sliders size-3" />
          Configurar
        </button>
      </div>
    </div>
  </CardLayout>
</template>
